/**
 * Pure rules of the import-review tagging loop: what a recipe's tags are now,
 * what applying a decision writes, and which suggestions are safe to accept in
 * bulk. Shared by the API (which writes) and the review screen (which draws).
 */
import { canonicalCuisine, EVERYDAY_CUISINE, type CuisineChoice } from './cuisineVocabulary';
import { canonicalDishTypes, type CanonicalDishType } from './dishTypeVocabulary';
import type {
  ContentGap,
  DietChoice,
  FieldState,
  MetadataCheck,
  MetadataDecisions,
  MetadataField,
  MetadataReview,
  MetadataSuggestion,
  ReviewField,
  ReviewMode,
  ReviewOutcome,
  ReviewSource,
  RowState,
  TimeValue,
} from '../types/recipeMetadata';
import { METADATA_FIELDS, REVIEW_FIELDS } from '../types/recipeMetadata';
import { hasRealTime, isDefaultTime, type StoredTiming, type TimingUpdate } from './recipeTimings';
import { deriveTimes, type RecipeTimeline, type TimelineStatus } from './recipeTimeline';

/** "Accept confident suggestions" takes a field at or above this. */
export const BULK_ACCEPT_CONFIDENCE = 85;

/** What a recipe holds of its step timeline (#850): the hands-on time, and a disputed timeline waiting for a person. */
export interface StoredTimeline {
  handsOnMinutes?: number | null;
  readyInMinutesMin?: number | null;
  timelineStatus?: TimelineStatus;
  timelineSuggestion?: RecipeTimeline;
  timelineChecks?: string[];
}

/** What a reviewer's time answer writes: the times, and the end of any dispute. */
export type TimeAnswerUpdate = TimingUpdate & {
  handsOnMinutes?: number;
  readyInMinutesMin?: number;
  timelineStatus?: 'person';
};

export interface TaggableRecipe extends StoredTiming, StoredTimeline {
  cuisines?: unknown[];
  dishTypes?: unknown[];
  diets?: unknown[];
  metadataSuggestion?: MetadataSuggestion;
  metadataReview?: MetadataReview;
}

const VEG_TAGS = /^(vegetarian|vegan|vegetarien(ne)?|vegetalien(ne)?|vegane?)$/i;
const FISH_TAGS = /^(fish|pescatarian|pescetarian|seafood|poisson|fruits de mer)$/i;

function stringsOf(raw: unknown[] | undefined): string[] {
  return (raw ?? []).filter((v): v is string => typeof v === 'string');
}

/** The value each field holds today, or null when it holds nothing we can read. */
export function currentValue(recipe: TaggableRecipe, field: 'cuisine'): CuisineChoice | null;
export function currentValue(recipe: TaggableRecipe, field: 'dishType'): CanonicalDishType | null;
export function currentValue(recipe: TaggableRecipe, field: 'diet'): DietChoice | null;
export function currentValue(recipe: TaggableRecipe, field: MetadataField): string | null;
export function currentValue(recipe: TaggableRecipe, field: MetadataField): string | null {
  const review = recipe.metadataReview;
  if (field === 'cuisine') {
    if (review?.cuisine === EVERYDAY_CUISINE) return EVERYDAY_CUISINE;
    return canonicalCuisine(stringsOf(recipe.cuisines));
  }
  if (field === 'dishType') {
    return canonicalDishTypes(recipe.dishTypes)[0] ?? null;
  }
  const diets = stringsOf(recipe.diets).map(d => d.trim());
  if (diets.some(d => VEG_TAGS.test(d))) return 'veg';
  if (diets.some(d => FISH_TAGS.test(d))) return 'fish';
  return review?.diet ?? null;
}

/** Whether a value is on record for this field, however it got there (any mode). */
export function isReviewed(recipe: TaggableRecipe, field: MetadataField): boolean {
  return recipe.metadataReview?.[field] != null;
}

/**
 * The fields a decision writes onto the recipe. Only the fields that change
 * are returned; a confirmed value the recipe already holds writes nothing.
 */
export function applyDecisions(
  recipe: TaggableRecipe,
  decisions: MetadataDecisions,
  options: { timeTyped?: boolean } = {},
): { cuisines?: string[]; dishTypes?: string[]; diets?: string[] } & TimeAnswerUpdate {
  const update: { cuisines?: string[]; dishTypes?: string[]; diets?: string[] } & TimeAnswerUpdate = {};

  if (decisions.cuisine) {
    // "Everyday" is a review result, never a cuisine: it clears the field.
    const next = decisions.cuisine === EVERYDAY_CUISINE ? [] : [decisions.cuisine];
    const now = stringsOf(recipe.cuisines);
    if (now.length !== next.length || now.some((c, i) => c !== next[i])) update.cuisines = next;
  }

  if (decisions.dishType) {
    // A recipe tagged ["side dish", "salad"] confirmed as a salad keeps both:
    // the pair is what makes it an accompaniment (#746).
    if (!canonicalDishTypes(recipe.dishTypes).includes(decisions.dishType)) {
      update.dishTypes = [decisions.dishType];
    }
  }

  if (decisions.diet) {
    const now = stringsOf(recipe.diets);
    const others = now.filter(d => !VEG_TAGS.test(d.trim()) && !FISH_TAGS.test(d.trim()));
    let kind: string[] = [];
    if (decisions.diet === 'veg') {
      // Keep a vegan tag: vegan is a vegetarian dish, and the stricter word is true.
      kind = now.some(d => /^vegan$/i.test(d.trim())) ? ['vegan', 'vegetarian'] : ['vegetarian'];
    } else if (decisions.diet === 'fish') {
      kind = ['fish'];
    }
    const next = [...others, ...kind];
    if (now.length !== next.length || now.some((d, i) => d !== next[i])) update.diets = next;
  }

  if (decisions.time) Object.assign(update, applyTime(recipe, decisions.time, { typed: options.timeTyped }));

  return update;
}

const whole = (n: unknown): number => (typeof n === 'number' && Number.isFinite(n) && n > 0 ? Math.round(n) : 0);

/** A week: preserved and cured things take days. Anything longer is a typo. */
export const MAX_TIME_MINUTES = 10080;

/**
 * A time a person may write: whole minutes, a total, and parts that fit in it.
 * Null otherwise. Préparation includes the time at the stove (#850), so the
 * two parts overlap and the total only has to cover the longer of them; a
 * blank total in the editor still means prep + cook (timeFromFields).
 */
export function sanitizeTime(raw: unknown): TimeValue | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const ok = (n: unknown) => typeof n === 'number' && Number.isInteger(n) && n >= 0 && n <= MAX_TIME_MINUTES;
  const prep = r.prep ?? 0;
  const cook = r.cook ?? 0;
  if (!ok(prep) || !ok(cook) || !ok(r.total)) return null;
  const time = { prep: prep as number, cook: cook as number, total: r.total as number };
  if (time.total === 0 || Math.max(time.prep, time.cook) > time.total) return null;
  return time;
}

/** '' is 0; anything that is not a whole number is NaN, which sanitizeTime refuses. */
const asMinutes = (text: string): number => (text.trim() === '' ? 0 : Number(text.trim()));

/** What the Time editor's three fields would save (a blank total is prep + cook), or null. */
export function timeFromFields(prep: string, cook: string, total: string): TimeValue | null {
  const p = asMinutes(prep);
  const c = asMinutes(cook);
  return sanitizeTime({ prep: p, cook: c, total: total.trim() === '' ? p + c : asMinutes(total) });
}

/** The recipe's own time, when someone stated one (not missing, not the import's 30-minute filler). */
export function currentTime(recipe: StoredTiming & StoredTimeline): TimeValue | null {
  if (!hasRealTime(recipe)) return null;
  return {
    // `handsOnMinutes` is « Préparation » (#850): a cook's edit sets it and a model-timed
    // recipe keeps it beside `preparationMinutes`. The old field is the fallback.
    prep: whole(recipe.handsOnMinutes) || whole(recipe.preparationMinutes),
    cook: whole(recipe.cookingMinutes),
    total: whole(recipe.readyInMinutes),
  };
}

/**
 * What a reviewed time writes: the three numbers, and `timeSource: 'person'`,
 * which is what tells every later reader (the re-scrape, the filler test) that
 * a person stated it. Written even when the numbers do not change.
 *
 * The reviewer's prep is the hands-on time (#850), and their total has no
 * range, so the shortest elapsed time is the total too. `timelineStatus:
 * 'person'` ends a dispute: the writer drops the disputed timeline with it.
 *
 * « Préparation » is written only where the recipe already shows one, a
 * dispute is open, or the reviewer typed the time (`typed`): a glance at a
 * page's prep is not a hands-on time, and would put a box on the recipe page.
 */
export function applyTime(
  recipe: StoredTiming & StoredTimeline,
  time: TimeValue,
  options: { typed?: boolean } = {},
): TimeAnswerUpdate {
  const update: TimeAnswerUpdate = {};
  const handsOn = whole(recipe.handsOnMinutes) > 0 || recipe.timelineStatus === 'review' || !!options.typed;
  if (whole(recipe.preparationMinutes) !== time.prep) update.preparationMinutes = time.prep;
  if (whole(recipe.cookingMinutes) !== time.cook) update.cookingMinutes = time.cook;
  if (whole(recipe.readyInMinutes) !== time.total) update.readyInMinutes = time.total;
  if (handsOn && whole(recipe.handsOnMinutes) !== time.prep) update.handsOnMinutes = time.prep;
  if (whole(recipe.readyInMinutesMin) !== time.total) update.readyInMinutesMin = time.total;
  if (recipe.timeSource !== 'person') update.timeSource = 'person';
  if (recipe.timelineStatus !== 'person') update.timelineStatus = 'person';
  return update;
}

/**
 * Two totals agree within 5 minutes, or 15% of the right one: a four-hour
 * braise guessed at 3 h 50 is right. The review outcome and the eval both
 * judge a time with this, so the strip and the eval agree.
 */
export const TIME_AGREEMENT_MINUTES = 5;
export const TIME_AGREEMENT_SHARE = 0.15;

export function timesAgree(guess: number, right: number): boolean {
  return Math.abs(guess - right) <= Math.max(TIME_AGREEMENT_MINUTES, TIME_AGREEMENT_SHARE * right);
}

export function timeOutcome(suggested: TimeValue | null | undefined, human: TimeValue): ReviewOutcome {
  if (!suggested) return 'unsuggested';
  return timesAgree(suggested.total, human.total) ? 'accepted' : 'corrected';
}

export function outcomeOf(suggested: string | null | undefined, human: string): ReviewOutcome {
  if (suggested == null) return 'unsuggested';
  return suggested === human ? 'accepted' : 'corrected';
}

/** The suggestion for one field, when there is one worth showing. */
export function suggestedValue(recipe: TaggableRecipe, field: MetadataField): string | null {
  return recipe.metadataSuggestion?.[field]?.value ?? null;
}

/** How a field was settled, or null when nobody settled it. */
export function fieldMode(recipe: TaggableRecipe, field: ReviewField): ReviewMode | null {
  const review = recipe.metadataReview;
  if (review?.[field] == null) return null;
  return review.fieldModes?.[field] ?? review.reviewMode ?? null;
}

/**
 * A person settled this field, looking at the row (`single`) or the page
 * (`glance`) — or, for the time, typed it in the recipe editor.
 */
export function isConfirmed(recipe: TaggableRecipe, field: ReviewField): boolean {
  return fieldState(recipe, field) === 'confirmed';
}

/**
 * The numbers of a disputed timeline, as the time a reviewer is shown: the
 * model's hands-on and ready-in. The timeline has no cook time of its own
 * (Préparation already holds it), so the page's stays: the editor opens on
 * these, and saving them as shown must not erase it.
 * The stored numbers were left as they were, so the model's are only here.
 */
function disputedTime(recipe: TaggableRecipe): TimeValue | null {
  if (recipe.timelineStatus !== 'review' || !recipe.timelineSuggestion) return null;
  const { handsOnMinutes, readyInMinutes } = deriveTimes(recipe.timelineSuggestion);
  return { prep: handsOnMinutes, cook: whole(recipe.cookingMinutes), total: readyInMinutes };
}

/** The stored time is a timeline's that passed its checks (#850): nobody needs to look, and nobody stated it. */
export const isModelTimed = (recipe: TaggableRecipe): boolean =>
  recipe.timelineStatus !== 'review' && (recipe.timeSource === 'model' || recipe.timelineStatus === 'auto');

/**
 * The time suggestion, unless it is stale. Stale is:
 *   - an estimate made while the recipe had no time, before a re-scrape found
 *     the page's: the page's time then speaks for itself;
 *   - anything on a model-timed recipe: the suggestion predates the timeline;
 *   - a total the stored time no longer holds, when nothing says that time is
 *     still the page's (or the filler): something has replaced it since.
 */
export function liveTimeSuggestion(recipe: TaggableRecipe): MetadataSuggestion['time'] | undefined {
  const disputed = disputedTime(recipe);
  if (disputed) return { value: disputed, confidence: 0, source: 'model' };
  const s = recipe.metadataSuggestion?.time;
  if (!s?.value) return undefined;
  if (isModelTimed(recipe)) return undefined;
  if (!hasRealTime(recipe)) return s;
  if (s.source !== 'publisher') return undefined;
  const pageOwned = recipe.timeSource === 'page' || recipe.timeSource === 'default';
  if (!pageOwned && s.value.total !== whole(recipe.readyInMinutes)) return undefined;
  return s;
}

/**
 * The time's state. Unlike a tag, a time stated by the page is a fact about
 * the recipe with no model needed: sure, unless the numbers contradict each
 * other (timingChecks). Missing means no time at all, or the import's filler.
 */
function timeState(recipe: TaggableRecipe): FieldState {
  // A disputed timeline leaves the stored numbers as they were, and a person has
  // not looked yet, even at a time a person set before the estimate existed (#850).
  if (recipe.timelineStatus === 'review') return 'unsure';
  const mode = fieldMode(recipe, 'time');
  if (mode === 'single' || mode === 'glance' || recipe.timeSource === 'person') return 'confirmed';
  // The timeline already passed its checks; the page's parts beside it prove nothing.
  if (isModelTimed(recipe) && hasRealTime(recipe)) return 'sure';
  const s = liveTimeSuggestion(recipe);
  if (s) return s.confidence >= BULK_ACCEPT_CONFIDENCE ? 'sure' : 'unsure';
  if (hasRealTime(recipe)) return timingChecks(recipe).length === 0 ? 'sure' : 'unsure';
  return 'missing';
}

/** The time a row shows: settled, else the live suggestion, else what the recipe holds. */
export function shownTime(recipe: TaggableRecipe): TimeValue | null {
  if (timeState(recipe) === 'confirmed') return currentTime(recipe) ?? recipe.metadataReview?.time ?? null;
  return liveTimeSuggestion(recipe)?.value ?? currentTime(recipe);
}

/** How the review screen draws a field. See FieldState. */
export function fieldState(recipe: TaggableRecipe, field: ReviewField): FieldState {
  if (field === 'time') return timeState(recipe);
  const mode = fieldMode(recipe, field);
  if (mode === 'single' || mode === 'glance') return 'confirmed';
  if (mode === 'bulk') return 'auto';
  const s = recipe.metadataSuggestion?.[field];
  if (s?.value != null) return s.confidence >= BULK_ACCEPT_CONFIDENCE ? 'sure' : 'unsure';
  // A value nobody (no model, no person) has checked is a guess like any other.
  return currentValue(recipe, field) != null ? 'unsure' : 'missing';
}

/** The value a field shows: settled, else suggested, else what the recipe holds. */
export function shownValue(recipe: TaggableRecipe, field: MetadataField): string | null {
  if (isReviewed(recipe, field)) return currentValue(recipe, field) ?? recipe.metadataReview?.[field] ?? null;
  return suggestedValue(recipe, field) ?? currentValue(recipe, field);
}

/** Fields that must be tapped before Accept: the AI is unsure, or there is nothing. */
export function acceptBlockers(recipe: TaggableRecipe): ReviewField[] {
  return REVIEW_FIELDS.filter((f) => {
    const state = fieldState(recipe, f);
    return state === 'unsure' || state === 'missing';
  });
}

/**
 * What Accept (and "These N look right") confirms: every field that is sure
 * or was auto-accepted, with the value it shows. An unsure or missing field is
 * never taken — the reviewer has to tap it first (acceptBlockers). Nor is a
 * model-timed recipe's time: confirming it would freeze the model's numbers
 * as a person's, and no person stated them.
 */
export function rowDecisions(recipe: TaggableRecipe): MetadataDecisions {
  const out: MetadataDecisions = {};
  for (const field of METADATA_FIELDS) {
    const state = fieldState(recipe, field);
    if (state !== 'sure' && state !== 'auto') continue;
    const value = shownValue(recipe, field);
    if (value != null) (out as Record<string, string>)[field] = value;
  }
  if (timeState(recipe) === 'sure' && !isModelTimed(recipe)) {
    const time = shownTime(recipe);
    if (time) out.time = time;
  }
  return out;
}

/** True when a person settled every field, the time included. */
export function isFullyConfirmed(recipe: TaggableRecipe): boolean {
  return REVIEW_FIELDS.every((f) => isConfirmed(recipe, f));
}

/**
 * Nothing left for a person: every field confirmed, or every tag confirmed
 * beside a timeline's time, which Accept never takes (rowDecisions).
 */
function isSettled(recipe: TaggableRecipe): boolean {
  if (isFullyConfirmed(recipe)) return true;
  return METADATA_FIELDS.every((f) => isConfirmed(recipe, f)) && isModelTimed(recipe) && timeState(recipe) === 'sure';
}

/**
 * A row still waiting for the model: nothing suggested and not every field
 * confirmed — or a suggestion made before times were reviewed, on a recipe
 * that has no time of its own. A suggestion whose time the model declined
 * (`value: null`) is not asked again.
 */
export function needsSuggestion(recipe: TaggableRecipe): boolean {
  const s = recipe.metadataSuggestion;
  if (!s) return !isSettled(recipe);
  return s.time === undefined && timeState(recipe) === 'missing';
}

const TIMING_CHECKS: ReadonlySet<MetadataCheck['code']> = new Set([
  'prep_exceeds_total', 'parts_exceed_total', 'total_default', 'total_missing', 'timeline_disputed', 'timeline_person',
]);

/** "28/65 · under_stated": the disputed timeline's hands-on and ready-in, then why. Data; the sentence comes from t(). */
function timelineDispute(recipe: TaggableRecipe): MetadataCheck | null {
  if (recipe.timelineStatus !== 'review') return null;
  const times = disputedTime(recipe);
  const parts = [times && `${times.prep}/${times.total}`, (recipe.timelineChecks ?? []).join(', ')].filter(Boolean);
  const code = recipe.timeSource === 'person' ? 'timeline_person' : 'timeline_disputed';
  return { code, ...(parts.length > 0 && { detail: parts.join(' · ') }) };
}

/**
 * The warnings worth showing. A timing warning was true when the suggestion
 * was made; once the time is confirmed or sure, the Time chip says what it is
 * and the warning only repeats an answered question.
 */
export function visibleChecks(recipe: TaggableRecipe): MetadataCheck[] {
  const dispute = timelineDispute(recipe);
  const checks = [...(recipe.metadataSuggestion?.checks ?? []), ...(dispute ? [dispute] : [])];
  const state = timeState(recipe);
  if (state !== 'confirmed' && state !== 'sure') return checks;
  return checks.filter((c) => !TIMING_CHECKS.has(c.code));
}

/**
 * The per-field modes after a review. A legacy document (one recipe-level
 * mode, no `fieldModes`) is backfilled first, so the fields this review does
 * not touch keep the mode they were settled with.
 */
export function nextFieldModes(
  previous: Partial<MetadataReview> | undefined,
  decided: ReviewField[],
  mode: ReviewMode,
): Partial<Record<ReviewField, ReviewMode>> {
  const out: Partial<Record<ReviewField, ReviewMode>> = { ...(previous?.fieldModes ?? {}) };
  for (const field of REVIEW_FIELDS) {
    if (out[field] == null && previous?.[field] != null && previous.reviewMode) out[field] = previous.reviewMode;
  }
  for (const field of decided) out[field] = mode;
  return out;
}

interface ContentCheckable {
  image?: unknown;
  ingredients?: unknown[];
  extendedIngredients?: unknown[];
  instructions?: unknown[];
}

const stepHasText = (s: unknown): boolean =>
  typeof s === 'string' ? s.trim().length > 0
    : !!s && typeof s === 'object' && typeof (s as { step?: unknown }).step === 'string'
      && ((s as { step: string }).step.trim().length > 0);

/**
 * What is missing from the recipe itself. A step counts only with text: 27
 * catalogue recipes once held the right number of blank steps.
 */
export function contentGaps(r: ContentCheckable): ContentGap[] {
  const gaps: ContentGap[] = [];
  if (!(r.ingredients?.length || r.extendedIngredients?.length)) gaps.push('ingredients');
  if (!(r.instructions ?? []).some(stepHasText)) gaps.push('steps');
  if (typeof r.image !== 'string' || !r.image) gaps.push('image');
  return gaps;
}

export const BLOCKING_GAPS: readonly ContentGap[] = ['ingredients', 'steps'];

/**
 * Where a row sits. First match wins:
 *   ready-to-publish — an import already accepted
 *   blocked          — no ingredients or no steps: cannot be accepted
 *   to-check         — a field is unsure or missing
 *   done             — a library or catalogue row a person settled entirely
 *   looks-right      — no warning and no gap: offered to "These N look right"
 *   ready            — settled or sure, but with a warning or gap to look at
 */
export function rowState(
  recipe: TaggableRecipe & { reviewStatus?: string },
  source: ReviewSource,
  gaps: ContentGap[],
): RowState {
  if (source === 'import' && recipe.reviewStatus === 'approved') return 'ready-to-publish';
  if (gaps.some((g) => BLOCKING_GAPS.includes(g))) return 'blocked';
  if (acceptBlockers(recipe).length > 0) return 'to-check';
  if (source !== 'import' && isSettled(recipe)) return 'done';
  if (gaps.length === 0 && visibleChecks(recipe).length === 0) return 'looks-right';
  return 'ready';
}

/**
 * Timing problems a rule can see without a model. Imports default a missing
 * total to 30 minutes (`api/recipes/import.ts`), so a bare 30 with no prep
 * and no cook beside it is a placeholder, not a measurement.
 */
export function timingChecks(r: StoredTiming & Pick<StoredTimeline, 'handsOnMinutes'>): MetadataCheck[] {
  // « Préparation » (hands-on) is what a cook edits and the page shows; the old field is the fallback.
  const handsOn = whole(r.handsOnMinutes);
  const prep = handsOn || whole(r.preparationMinutes);
  const cook = r.cookingMinutes ?? 0;
  const total = r.readyInMinutes ?? 0;
  if (!total) return [{ code: 'total_missing' }];
  if (prep > total) return [{ code: 'prep_exceeds_total', detail: `${prep} > ${total}` }];
  // Préparation includes the stove, so beside it the cook only has to fit on its own (sanitizeTime).
  if (handsOn && cook > total) return [{ code: 'parts_exceed_total', detail: `${cook} > ${total}` }];
  if (!handsOn && prep + cook > total) return [{ code: 'parts_exceed_total', detail: `${prep} + ${cook} > ${total}` }];
  if (isDefaultTime(r)) return [{ code: 'total_default' }];
  return [];
}
